import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { SearchResult } from '../../shared/models/search-result';
import { UiStateStore } from '../../shared/store/ui-state-store.service';
import { environment } from '../../../environments/environment';
import { CandidateSummary } from '../models/candidate-summary';
import { CandidatesFilter } from '../models/candidates-filter';
import { Resume } from '../../shared/models/resume';
import { ProfileBookmark } from '../models/profile-bookmark';
import { ProgressStatuses } from '../../tasks-wizard/models/progress-statuses';
import { JobProgressStatus } from '../../shared/models/jobProgressStatus';

@Injectable({
  providedIn: 'root'
})
export class DataService {

  constructor(private httpClient: HttpClient, private uiState: UiStateStore) { }

  searchCandidates(searchKeyword: string, location: string): Observable<CandidateSummary[]> {
    const url = `${environment.baseApiUrl}/candidates/search/${searchKeyword}/locations/${location}`;
    return this.httpClient.get<CandidateSummary[]>(url)
      .pipe(map(response => {
        return response.map(resp => CandidateSummary.parse(resp));
      }),
        catchError(err => {
          this.uiState.setError(`Error when searching candidates: ${err["message"]}`);
          return throwError(err);
        }));
  }

  filterCandidates(candidatesFilter: CandidatesFilter): Observable<SearchResult<CandidateSummary>> {
    const url = `${environment.baseApiUrl}/candidates/search/filter`;
    return this.httpClient.post<SearchResult<CandidateSummary>>(url, candidatesFilter)
      .pipe(map(response => {
        return SearchResult.parse<CandidateSummary>(response, new CandidateSummary());
      }),
        catchError(err => {
          this.uiState.setError(`Error when filtering candidates: ${err["message"]}`);
          return throwError(err);
        }));
  }

  getResume(resumeId: string): Observable<Resume> {
    const url = `${environment.baseApiUrl}/resume/${resumeId}`;
    return this.httpClient.get<Resume>(url)
      .pipe(map(response => {
        return Resume.parse(response);
      }),
        catchError(err => {
          this.uiState.setError(`Error when getting Resume: ${err["message"]}`);
          return throwError(err);
        }));
  }

  bookmarkCandidate(bookmark: ProfileBookmark): Observable<ProfileBookmark> {
    const url = `${environment.baseApiUrl}/profiles/bookmark`;
    return this.httpClient.post<ProfileBookmark>(url, bookmark)
      .pipe(map(resp => resp),
        catchError(err => {
          this.uiState.setError(`Error when bookmark candidate profile: ${err["message"]}`);
          return throwError(err);
        }));
  }

  unBookmarkCandidate(bookmarkId: string): Observable<any> {
    const url = `${environment.baseApiUrl}/profiles/unbookmark/${bookmarkId}`;
    return this.httpClient.get<any>(url)
      .pipe(map(resp => resp),
        catchError(err => {
          this.uiState.setError(`Error when unbookmark candidate profile: ${err["message"]}`);
          return throwError(err);
        }));
  }

  getProfileBookmarks(accountId: string): Observable<ProfileBookmark[]> {
    const url = `${environment.baseApiUrl}/profiles/bookmarks/${accountId}`;
    return this.httpClient.get<ProfileBookmark[]>(url)
      .pipe(map(response => {
        return response.map(resp => ProfileBookmark.parse(resp));
      }),
        catchError(err => {
          this.uiState.setError(`Error when getting ProfileBookmarks: ${err["message"]}`);
          return throwError(err);
        }));
  }

  getMatchedCandidates(jobId: string, pageNumber: number): Observable<SearchResult<CandidateSummary>> {
    const url = `${environment.baseApiUrl}/candidates/matchlist/${jobId}/${pageNumber}`;
    return this.httpClient.get<SearchResult<CandidateSummary>>(url)
      .pipe(map(response => {
        return SearchResult.parse<CandidateSummary>(response, new CandidateSummary());
      }),
        catchError(err => {
          this.uiState.setError(`Error when getting jobs matched candidates: ${err["message"]}`);
          return throwError(err);
        }));
  }

  getJobProgressStatus(jobId: string): Observable<JobProgressStatus> {
    const url = `${environment.baseApiUrl}/jobs/${jobId}/progressstatus`;
    return this.httpClient.get<JobProgressStatus>(url)
      .pipe(map(response => {
        return JobProgressStatus.parse(response);
      }),
        catchError(err => {
          this.uiState.setError(`Error when getting job progress status: ${err["message"]}`);
          return throwError(err);
        }));
  }

}
